import {
  Skeleton,
  SkeletonItem,
  Tooltip,
  mergeClasses,
} from "@fluentui/react-components";
import {
  HomeIcon,
  PortfolioIcon,
  PracticeIcon,
} from "apps/project-iq/components/icons/ProjectIqIcons";
import { useLeadershipDashboardAccess } from "apps/project-iq/hooks/useLeadershipDashboardAccess";
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useStyles } from "./SidebarNavigation.styles";
import {
  ISidebarNavigationItem,
  ISidebarNavigationProps,
} from "./SidebarNavigation.types";

const HOME_PATH = "/";
const PORTFOLIOS_PATH = "/leadership/portfolios";
const PRACTICES_PATH = "/leadership/practices";
const EXPANDED_ICON_SIZE = 15;
const COLLAPSED_ICON_SIZE = 18;
const SKELETON_ROW_COUNT = 2;
const SKELETON_ROW_KEYS = Array.from(
  { length: SKELETON_ROW_COUNT },
  (_, i) => `sidebar-navigation-skeleton-row-${i}`,
);

interface ISidebarNavigationSkeletonProps {
  isVisuallyExpanded: boolean;
  rowCount: number;
}

const SidebarNavigationSkeleton = ({
  isVisuallyExpanded,
  rowCount,
}: ISidebarNavigationSkeletonProps): JSX.Element => {
  const styles = useStyles();

  return (
    <>
      {SKELETON_ROW_KEYS.slice(0, rowCount).map((key) => (
        <Skeleton key={key} aria-hidden="true">
          <div
            className={mergeClasses(
              styles.skeletonRow,
              !isVisuallyExpanded && styles.skeletonRowCollapsed,
            )}
          >
            <SkeletonItem shape="circle" className={styles.skeletonIcon} />
            {isVisuallyExpanded && (
              <SkeletonItem animation="wave" className={styles.skeletonLabel} />
            )}
          </div>
        </Skeleton>
      ))}
    </>
  );
};

export const SidebarNavigation = ({
  isVisuallyExpanded,
}: ISidebarNavigationProps): JSX.Element => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPortfolioAccess, hasPracticeAccess, isLoading } =
    useLeadershipDashboardAccess();

  const iconSize = isVisuallyExpanded
    ? EXPANDED_ICON_SIZE
    : COLLAPSED_ICON_SIZE;

  const items = useMemo<ISidebarNavigationItem[]>(
    () =>
      [
        {
          id: "home",
          label: "Home",
          path: HOME_PATH,
          icon: <HomeIcon size={iconSize} />,
        },
        hasPortfolioAccess
          ? {
              id: "portfolios",
              label: "Portfolios",
              path: PORTFOLIOS_PATH,
              icon: <PortfolioIcon size={iconSize} />,
            }
          : null,
        hasPracticeAccess
          ? {
              id: "practices",
              label: "Practices",
              path: PRACTICES_PATH,
              icon: <PracticeIcon size={iconSize} />,
            }
          : null,
      ].filter((item): item is ISidebarNavigationItem => item !== null),
    [hasPortfolioAccess, hasPracticeAccess, iconSize],
  );

  const leadershipItemCount = items.length - 1;
  const pendingRowCount = isLoading
    ? Math.max(0, SKELETON_ROW_COUNT - leadershipItemCount)
    : 0;

  return (
    <nav className={styles.section} aria-label="Project IQ navigation">
      <div className={styles.list}>
        {items.map((item) => {
          const isSelected = location.pathname === item.path;
          const handleSelect = () => navigate(item.path);

          const itemEl = (
            <div
              role="button"
              tabIndex={0}
              className={mergeClasses(
                styles.item,
                !isVisuallyExpanded && styles.itemCollapsed,
                isSelected && styles.itemSelected,
              )}
              onClick={handleSelect}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleSelect();
                }
              }}
              aria-label={`Go to ${item.label}`}
              aria-current={isSelected ? "page" : undefined}
            >
              <span
                className={mergeClasses(
                  styles.itemIcon,
                  isSelected && styles.itemIconSelected,
                )}
              >
                {item.icon}
              </span>
              {isVisuallyExpanded && (
                <span className={styles.itemLabel}>{item.label}</span>
              )}
            </div>
          );

          return isVisuallyExpanded ? (
            <div key={item.id}>{itemEl}</div>
          ) : (
            <Tooltip
              key={item.id}
              content={item.label}
              relationship="label"
              positioning="after"
            >
              {itemEl}
            </Tooltip>
          );
        })}

        {pendingRowCount > 0 && (
          <SidebarNavigationSkeleton
            isVisuallyExpanded={isVisuallyExpanded}
            rowCount={pendingRowCount}
          />
        )}
      </div>
    </nav>
  );
};
